"use client";
import React, { useEffect, useRef, useState } from "react";
import { IMaskInput } from "react-imask";

type Turnstile = {
    render?: (
        container: string | HTMLElement,
        options: {
            sitekey: string;
            callback?: (token: string) => void;
            "expired-callback"?: () => void;
            "error-callback"?: () => void;
        },
    ) => string;
    reset?: (widgetId?: string) => void;
    remove?: (widgetId?: string) => void;
};

function ApplyNowForm() {
    const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!;
    const formRef = useRef<HTMLFormElement | null>(null);

    // Turnstile best-practice: keep the widget id returned from render()
    const turnstileWidgetIdRef = useRef<string | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    // Optional (but helpful): keep the token in state, don’t rely only on FormData lookup timing
    const [turnstileToken, setTurnstileToken] = useState<string>("");

    const resetTurnstile = () => {
        if (typeof window === "undefined") return;

        const t = (window as Window & { turnstile?: Turnstile }).turnstile;
        const widgetId = turnstileWidgetIdRef.current;

        // Reset by widgetId (correct). Also clear our token since it’s no longer valid.
        if (t?.reset && widgetId) t.reset(widgetId);
        setTurnstileToken("");
    };

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!isMounted) return;

        let cancelled = false;

        const tryRenderTurnstile = () => {
            if (typeof window === "undefined") return;

            const t = (window as Window & { turnstile?: Turnstile }).turnstile;
            const container = document.getElementById("apply-now-turnstile");

            if (!t?.render || !container) return;

            // Render only once (don’t rely on hasChildNodes(), widget could re-render differently)
            if (!turnstileWidgetIdRef.current) {
                const widgetId = t.render(container, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token) => setTurnstileToken(token),
                    "expired-callback": () => setTurnstileToken(""),
                    "error-callback": () => setTurnstileToken(""),
                });

                turnstileWidgetIdRef.current = widgetId || null;
            }
        };

        // Try immediately
        tryRenderTurnstile();

        // And retry briefly until the Turnstile script is ready
        const interval = setInterval(() => {
            if (cancelled) return;
            const t = (window as Window & { turnstile?: Turnstile }).turnstile;
            if (t?.render) {
                tryRenderTurnstile();
                clearInterval(interval);
            }
        }, 100);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [isMounted, TURNSTILE_SITE_KEY]);

    const [isFocused, setIsFocused] = useState(false);

    const [mortgageReason, setMortgageReason] = useState("");
    const [isCustomer, setIsCustomer] = useState<boolean | null>(null);
    const [propertyLocation, setPropertyLocation] = useState("");

    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        let cleanValue = value;

        if (name === "first_name" || name === "last_name") {
            cleanValue = value.replace(/[^a-zA-Z\s]/g, "");
        } else if (name === "phone") {
            cleanValue = value.replace(/\D/g, "").slice(0, 10);
        }

        setForm((prev) => ({
            ...prev,
            [name]: cleanValue,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        const { first_name, last_name, phone, email } = form;
        const errors: string[] = [];

        if (!mortgageReason) errors.push("What you are getting in touch about");
        if (isCustomer === null) errors.push("Whether you are currently a ClearPath Lending customer");
        if (!propertyLocation) errors.push("Property Location");
        if (!first_name) errors.push("First Name");
        if (!last_name) errors.push("Last Name");
        if (!phone && !email) errors.push("Phone or Email");
        if (phone && phone.length !== 10) errors.push("Phone number must be exactly 10 digits");

        if (errors.length > 0) {
            setError(`Please fill in: ${errors.join(", ")}`);
            setSuccess("");
            resetTurnstile();
            return;
        }

        // Prefer the token we captured from Turnstile callbacks.
        // As a fallback, still read from the hidden input.
        let token = turnstileToken;

        if (!token) {
            const formEl = formRef.current;
            const fd = formEl ? new FormData(formEl) : null;
            const raw = fd?.get("cf-turnstile-response");
            token = typeof raw === "string" ? raw : "";
        }

        if (!token) {
            setError("Please complete the CAPTCHA.");
            setSuccess("");
            resetTurnstile();
            return;
        }

        setIsSubmitting(true);
        setError("");
        setSuccess("");

        const payload = {
            mortgageReason,
            isCurrentCustomer: isCustomer,
            firstName: first_name,
            lastName: last_name,
            email,
            phoneNumber: phone ? phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3") : undefined,
            propertyLocation,
            captchaToken: token,
        };

        try {
            const response = await fetch("https://cpl-api.blinklos.com/website/apply-now", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const result = await response.json().catch(() => null);
                throw new Error((result && result.error) || "Failed to submit the form.");
            }

            setForm({ first_name: "", last_name: "", phone: "", email: "" });
            setMortgageReason("");
            setIsCustomer(null);
            setPropertyLocation("");
            setError("");
            setSuccess("Thank you! We got your application and we will reach out to you ASAP");
            resetTurnstile();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Submission failed. Please try again.");
            setSuccess("");
            resetTurnstile();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="bg-white w-full pb-24 px-4 sm:px-6 lg:px-8">
            <div className="mx-auto w-full max-w-[330px] sm:max-w-[640px] lg:max-w-[960px] xl:max-w-[1100px] 2xl:max-w-[1200px] font-medium">
                {/* Heading */}
                <h2 className=" w-[300px] sm:w-full h-[80px] text-center mb-8 text-[#212459] text-[30px] font-bold uppercase lg:text-3xl 2xl:w-[1200px] 2xl:h-[40px] 2xl:text-[30px]">
                    Call us now at 855-866-5363.
                </h2>

                <form ref={formRef} aria-busy={isSubmitting} onSubmit={handleSubmit} className="2xl:pl-[95px]">
                    <div className="2xl:mb-4">
                        <p
                            id="contact_about_label"
                            className="w-[300px] sm:w-full h-[39px] block text-[15px] font-medium text-black mb-2 2xl:w-[402px] 2xl:h-[20px]"
                        >
                            {`FIRST, TELL US WHAT YOU'RE GETTING IN TOUCH ABOUT.`}
                        </p>
                        <div
                            role="radiogroup"
                            aria-labelledby="contact_about_label"
                            aria-required="true"
                            className="p-2 text-[13px] flex sm:flex-col flex-row pl-0 2xl:flex-row 2xl:w-[500px]"
                        >
                            <label className="inline-flex items-center gap-2 cursor-pointer relative">
                                <input
                                    type="radio"
                                    id="contact_about_buying"
                                    name="contact_about"
                                    value="BUYING"
                                    checked={mortgageReason === "Buying"}
                                    onChange={() => setMortgageReason("Buying")}
                                    className="peer appearance-none w-[22px] h-[22px] border-[3px] border-[#1275B9] rounded-full transition-all duration-200 bg-[#f2f2f2]"
                                />
                                <span className="pointer-events-none w-[10px] h-[10px] bg-[#1275B9] rounded-full scale-0 peer-checked:scale-100 absolute left-[6px] top-[6px] transition-transform duration-200"></span>
                                <span className="w-[127px] h-[20px] uppercase 2xl:w-[220px]">BUYING A HOME</span>
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer relative">
                                <input
                                    type="radio"
                                    id="contact_about_refinancing"
                                    name="contact_about"
                                    value="REFINANCING"
                                    checked={mortgageReason === "Refinancing"}
                                    onChange={() => setMortgageReason("Refinancing")}
                                    className="peer appearance-none w-[22px] h-[22px] border-[3px] border-[#1275B9] rounded-full transition-all duration-200 bg-[#f2f2f2]"
                                />
                                <span className="pointer-events-none w-[10px] h-[10px] bg-[#1275B9] rounded-full scale-0 peer-checked:scale-100 absolute left-[6px] top-[6px] transition-transform duration-200"></span>
                                <span className="w-[127px] h-[20px] uppercase">Refinancing</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <p
                            id="is_customer_label"
                            className="w-[300px] sm:w-full h-[39px] block text-[15px] font-medium text-black mb-2 2xl:w-[411px] 2xl:h-[20px]"
                        >
                            ARE YOU CURRENTLY A CLEARPATH LENDING CUSTOMER?
                        </p>
                        <div
                            role="radiogroup"
                            aria-labelledby="is_customer_label"
                            aria-required="true"
                            className="p-2 text-[13px] flex sm:flex-col flex-row pl-0 2xl:flex-row"
                        >
                            <label className="inline-flex items-center gap-2 cursor-pointer relative">
                                <input
                                    type="radio"
                                    name="is_customer"
                                    id="is_customer_yes"
                                    value="YES"
                                    checked={isCustomer === true}
                                    onChange={() => setIsCustomer(true)}
                                    className="peer appearance-none w-[22px] h-[22px] border-[3px] border-[#1275B9] rounded-full transition-all duration-200 bg-[#f2f2f2]"
                                />
                                <span className="pointer-events-none w-[10px] h-[10px] bg-[#1275B9] rounded-full scale-0 peer-checked:scale-100 absolute left-[6px] top-[6px] transition-transform duration-200"></span>
                                <span className="w-[127px] h-[20px] uppercase 2xl:w-[220px]">YES</span>
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer relative">
                                <input
                                    type="radio"
                                    name="is_customer"
                                    id="is_customer_no"
                                    value="NO"
                                    checked={isCustomer === false}
                                    onChange={() => setIsCustomer(false)}
                                    className="peer appearance-none w-[22px] h-[22px] border-[3px] border-[#1275B9] rounded-full transition-all duration-200 bg-[#f2f2f2]"
                                />
                                <span className="pointer-events-none w-[10px] h-[10px] bg-[#1275B9] rounded-full scale-0 peer-checked:scale-100 absolute left-[6px] top-[6px] transition-transform duration-200"></span>
                                <span className="w-[127px] h-[20px] uppercase">NO</span>
                            </label>
                        </div>
                    </div>

                    {/* Name Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 py-4 2xl:w-[1000px]">
                        <div>
                            <label
                                htmlFor="first_name"
                                className="block text-[15px] font-medium text-black uppercase h-[20px]"
                            >
                                First Name
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                className="mt-1 rounded-md border-[3px] border-[#1275B9] bg-[#f7f7f7] p-2 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[481px]"
                                name="first_name"
                                value={form.first_name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="last_name"
                                className="block  font-medium text-black uppercase text-[15px] w-[82px] h-[20px]"
                            >
                                Last Name
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                className="mt-1 rounded-md border-[3px] border-[#1275B9] bg-[#f7f7f7] p-2 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[481px]"
                                name="last_name"
                                value={form.last_name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    {/* Contact Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5  2xl:w-[1000px]">
                        <div>
                            <label
                                htmlFor="phone"
                                className="block  font-medium text-black uppercase text-[15px] w-[120px] h-[20px]"
                            >
                                Phone Number
                            </label>

                            <p id="phone-or-email-hint" className="sr-only">
                                Provide either a phone number or an email address.
                            </p>
                            <IMaskInput
                                aria-describedby="phone-or-email-hint"
                                id="phone"
                                mask="(000) 000-0000"
                                type="tel"
                                lazy={!isFocused}
                                value={form.phone}
                                placeholder="xxx-xxx-xxxx"
                                unmask={false}
                                name="phone"
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => {
                                    if (!form.phone) setIsFocused(false);
                                }}
                                className="mt-1 rounded-md border-[3px] border-[#1275B9] bg-[#f7f7f7] p-2 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[316px]"
                                onAccept={(value: string) => {
                                    const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
                                    const syntheticEvent = {
                                        target: {
                                            name: "phone",
                                            value: digitsOnly,
                                        },
                                    } as React.ChangeEvent<HTMLInputElement>;
                                    handleChange(syntheticEvent);
                                }}
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="email"
                                className="block font-medium text-black uppercase text-[15px] w-[45px] h-[20px]"
                            >
                                Email
                            </label>
                            <input
                                aria-describedby="phone-or-email-hint"
                                id="email"
                                type="email"
                                className="mt-1 rounded-md  border-[3px] border-[#1275B9] bg-[#f7f7f7] p-2 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[316px]"
                                name="email"
                                placeholder="email@example.com"
                                value={form.email}
                                onChange={handleChange}
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="state"
                                className="block  font-medium text-black uppercase text-[15px] h-[20px]"
                            >
                                Property Location
                            </label>
                            <select
                                id="state"
                                name="state"
                                value={propertyLocation}
                                onChange={(e) => setPropertyLocation(e.target.value)}
                                className="mt-1 rounded-md  border-[3px] border-[#1275B9] bg-[#f7f7f7] p-0 w-[300px] sm:w-full h-[39px] text-[#818181] 2xl:w-[316px]"
                            >
                                <option value="" disabled>
                                    Please select
                                </option>
                                {[
                                    "AK",
                                    "AL",
                                    "AR",
                                    "AZ",
                                    "CA",
                                    "CO",
                                    "CT",
                                    "DC",
                                    "DE",
                                    "FL",
                                    "GA",
                                    "HI",
                                    "IA",
                                    "ID",
                                    "IL",
                                    "IN",
                                    "KS",
                                    "KY",
                                    "LA",
                                    "MA",
                                    "MD",
                                    "ME",
                                    "MI",
                                    "MN",
                                    "MO",
                                    "MS",
                                    "MT",
                                    "NC",
                                    "ND",
                                    "NE",
                                    "NH",
                                    "NJ",
                                    "NM",
                                    "NV",
                                    "NY",
                                    "OH",
                                    "OK",
                                    "OR",
                                    "PA",
                                    "RI",
                                    "SC",
                                    "SD",
                                    "TN",
                                    "TX",
                                    "UT",
                                    "VA",
                                    "VT",
                                    "WA",
                                    "WI",
                                    "WV",
                                    "WY",
                                ].map((state) => (
                                    <option key={state} value={state}>
                                        {state}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {isMounted ? (
                        <div className="w-full max-w-[316px] my-4">
                            <div
                                id="apply-now-turnstile"
                                className="cf-turnstile"
                                data-sitekey={TURNSTILE_SITE_KEY}
                                data-action="apply-now"
                            />
                        </div>
                    ) : (
                        <div className="w-full max-w-[316px] my-4" />
                    )}

                    {/* Submit */}
                    <div className="text-center pt-8 2xl:w-[995px]">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            aria-busy={isSubmitting}
                            className={`text-[15px] w-full sm:w-auto font-bold uppercase rounded-full px-6 py-2 transition ${
                                isSubmitting
                                    ? "bg-gray-600 cursor-not-allowed"
                                    : "bg-[#1275b9] text-white hover:bg-white hover:text-[#1275b9] hover:border hover:border-[#1275b9]"
                            }`}
                        >
                            {isSubmitting ? "Submitting..." : "Get Started"}
                        </button>

                        {/* Messages */}
                        <div className="mt-4 text-center text-2.9xl" aria-live="polite" aria-atomic="true">
                            {error ? (
                                <p className="text-red-600" role="alert">
                                    {error}
                                </p>
                            ) : null}
                            {success ? (
                                <p className="text-green-600" role="status">
                                    {success}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </form>
            </div>
        </section>
    );
}

export default ApplyNowForm;
